import {
  AttributionEventType,
  AttributionSpec,
  DestinationType,
  OptimizationGoal,
  OutcomeCampaignObjective,
} from '../facebook/AdDelivery';
import { CampaignGoalType } from './CampaignGoalType';

/**
 * What the ad set's `promoted_object` has to carry for this combination:
 * the business's page, a dataset + conversion event the AE picked, or nothing at all.
 */
export type PromotedObjectKind = 'page' | 'web-event' | 'none';

export interface OptimizationDefinition {
  /**
   * Stable identifier for the row. Keyed by (objective, performance goal) today; a slug
   * rather than the raw `OptimizationGoal` so a goal whose destination is ambiguous —
   * Conversations can be Messenger, WhatsApp or Instagram Direct — can become several rows
   * without adding a dropdown.
   */
  key: string;
  objective: OutcomeCampaignObjective;
  optimizationGoal: OptimizationGoal;
  /** Meta's name for the objective, shown on the objective card. */
  objectiveLabel: string;
  /** Meta's name for the performance goal, shown in the performance goal select. */
  label: string;
  /** What this combination does to delivery, in the analysts' language. */
  description: string;
  /** Plain-English destination for the review step, e.g. "sends people to your website". */
  destinationSummary: string;
  /**
   * The reporting bucket. Feeds funnel-stage attribution, the ad-spend emails and the
   * budget estimate's cost-per — it no longer decides anything about delivery.
   */
  reportingGoal: CampaignGoalType;
  /** Omitted where Meta expects no destination; `UNDEFINED` is read-only and must never be sent. */
  destinationType?: DestinationType;
  promotedObjectKind: PromotedObjectKind;
  /** Only creatives carrying a lead form can run on this combination. */
  requiresLeadCard: boolean;
  /** The cascading ad-group flow is offered on this combination. */
  supportsCascading: boolean;
  /**
   * Hide dynamic creatives carrying a SEE_MORE CTA — it sends nobody off-site, so it is
   * broken on a campaign optimizing for an off-site event. True on Engagement · Offsite
   * Conversions only, which is exactly where it fired before the catalog existed.
   */
  disallowsSeeMoreCTA: boolean;
  /** Meta accepts `frequency_control_specs` only alongside REACH. */
  usesFrequencyControl: boolean;
  attributionSpec: AttributionSpec[];
}

// Non-conversion goals only accept a 1-day click window.
const CLICK_1: AttributionSpec[] = [
  { event_type: AttributionEventType.CLICK_THROUGH, window_days: 1 },
];

const CONVERSION_WINDOW: AttributionSpec[] = [
  { event_type: AttributionEventType.CLICK_THROUGH, window_days: 7 },
  { event_type: AttributionEventType.VIEW_THROUGH, window_days: 1 },
];

const DEFAULT_FLAGS = {
  requiresLeadCard: false,
  supportsCascading: false,
  disallowsSeeMoreCTA: false,
  usesFrequencyControl: false,
};

/**
 * The combinations the builder offers, curated from what the team actually publishes by hand
 * rather than from a judgement about what drives what. Ten rows cover 96% of the ad sets built
 * in Ads Manager over the last 12 months.
 *
 * Every row consumes a creative shape the builder already produces — a link creative, or that
 * same creative with a lead form attached. Messaging and call ads need a third shape (a
 * messaging or phone destination on the creative) and are deliberately absent.
 *
 * Combination validity is Meta's to enforce: the dropdowns are constrained to this table and
 * publish surfaces Meta's own error. There is no local rule table, because Meta returns the
 * same "performance goal isn't available" string for a missing promoted object as for a genuinely
 * invalid goal, so any local table is wrong in both directions.
 */
export const optimizationCatalog: OptimizationDefinition[] = [
  {
    ...DEFAULT_FLAGS,
    key: 'awareness.reach',
    objective: OutcomeCampaignObjective.OUTCOME_AWARENESS,
    optimizationGoal: OptimizationGoal.REACH,
    objectiveLabel: 'Awareness',
    label: 'Reach',
    description:
      'Show the ad to as many different people as possible, capped by frequency.',
    destinationSummary: 'builds awareness — no click destination',
    reportingGoal: CampaignGoalType.InStoreSales,
    promotedObjectKind: 'page',
    usesFrequencyControl: true,
    attributionSpec: CLICK_1,
  },
  {
    ...DEFAULT_FLAGS,
    key: 'awareness.impressions',
    objective: OutcomeCampaignObjective.OUTCOME_AWARENESS,
    optimizationGoal: OptimizationGoal.IMPRESSIONS,
    objectiveLabel: 'Awareness',
    label: 'Impressions',
    description:
      'Show the ad as many times as possible, without capping how often one person sees it.',
    destinationSummary: 'builds awareness — no click destination',
    reportingGoal: CampaignGoalType.InStoreSales,
    promotedObjectKind: 'page',
    attributionSpec: CLICK_1,
  },
  {
    ...DEFAULT_FLAGS,
    key: 'traffic.landing_page_views',
    objective: OutcomeCampaignObjective.OUTCOME_TRAFFIC,
    optimizationGoal: OptimizationGoal.LANDING_PAGE_VIEWS,
    objectiveLabel: 'Traffic',
    label: 'Landing Page Views',
    description:
      'Find people likely to click through and wait for the page to load.',
    destinationSummary: 'sends people to your website',
    reportingGoal: CampaignGoalType.WebsiteSessions,
    destinationType: DestinationType.WEBSITE,
    promotedObjectKind: 'none',
    attributionSpec: CLICK_1,
  },
  {
    ...DEFAULT_FLAGS,
    key: 'traffic.link_clicks',
    objective: OutcomeCampaignObjective.OUTCOME_TRAFFIC,
    optimizationGoal: OptimizationGoal.LINK_CLICKS,
    objectiveLabel: 'Traffic',
    label: 'Link Clicks',
    description:
      'Find people likely to click the ad, whether or not the page finishes loading.',
    destinationSummary: 'sends people to your website',
    reportingGoal: CampaignGoalType.WebsiteSessions,
    destinationType: DestinationType.WEBSITE,
    promotedObjectKind: 'none',
    attributionSpec: CLICK_1,
  },
  {
    ...DEFAULT_FLAGS,
    key: 'engagement.offsite_conversions',
    objective: OutcomeCampaignObjective.OUTCOME_ENGAGEMENT,
    optimizationGoal: OptimizationGoal.OFFSITE_CONVERSIONS,
    objectiveLabel: 'Engagement',
    label: 'Offsite Conversions',
    description:
      'Find people likely to complete your chosen conversion event on your site.',
    destinationSummary: 'sends people to your website',
    reportingGoal: CampaignGoalType.WebsiteSessions,
    destinationType: DestinationType.WEBSITE,
    promotedObjectKind: 'web-event',
    disallowsSeeMoreCTA: true,
    attributionSpec: CONVERSION_WINDOW,
  },
  {
    ...DEFAULT_FLAGS,
    key: 'engagement.post_engagement',
    objective: OutcomeCampaignObjective.OUTCOME_ENGAGEMENT,
    optimizationGoal: OptimizationGoal.POST_ENGAGEMENT,
    objectiveLabel: 'Engagement',
    label: 'Post Engagement',
    description: 'Find people likely to react to, comment on or share the ad.',
    destinationSummary: 'keeps people on the ad itself',
    reportingGoal: CampaignGoalType.WebsiteSessions,
    promotedObjectKind: 'none',
    attributionSpec: CLICK_1,
  },
  {
    ...DEFAULT_FLAGS,
    key: 'leads.instant_forms',
    objective: OutcomeCampaignObjective.OUTCOME_LEADS,
    optimizationGoal: OptimizationGoal.LEAD_GENERATION,
    objectiveLabel: 'Leads',
    label: 'Instant Forms',
    description:
      'Find people likely to submit the lead form attached to the ad.',
    destinationSummary: 'collects a form on the ad',
    reportingGoal: CampaignGoalType.LeadGeneration,
    destinationType: DestinationType.ON_AD,
    promotedObjectKind: 'page',
    requiresLeadCard: true,
    supportsCascading: true,
    attributionSpec: CLICK_1,
  },
  {
    ...DEFAULT_FLAGS,
    key: 'leads.offsite_conversions',
    objective: OutcomeCampaignObjective.OUTCOME_LEADS,
    optimizationGoal: OptimizationGoal.OFFSITE_CONVERSIONS,
    objectiveLabel: 'Leads',
    label: 'Offsite Conversions',
    description:
      'Find people likely to complete your chosen lead event on your site.',
    destinationSummary: 'sends people to your website',
    reportingGoal: CampaignGoalType.LeadGeneration,
    destinationType: DestinationType.WEBSITE,
    promotedObjectKind: 'web-event',
    attributionSpec: CONVERSION_WINDOW,
  },
  {
    ...DEFAULT_FLAGS,
    key: 'sales.offsite_conversions',
    objective: OutcomeCampaignObjective.OUTCOME_SALES,
    optimizationGoal: OptimizationGoal.OFFSITE_CONVERSIONS,
    objectiveLabel: 'Sales',
    label: 'Offsite Conversions',
    description:
      'Find people likely to complete your chosen purchase event on your site.',
    destinationSummary: 'sends people to your website',
    reportingGoal: CampaignGoalType.ECommerceSales,
    destinationType: DestinationType.WEBSITE,
    promotedObjectKind: 'web-event',
    attributionSpec: CONVERSION_WINDOW,
  },
  {
    ...DEFAULT_FLAGS,
    key: 'sales.link_clicks',
    objective: OutcomeCampaignObjective.OUTCOME_SALES,
    optimizationGoal: OptimizationGoal.LINK_CLICKS,
    objectiveLabel: 'Sales',
    label: 'Link Clicks',
    description:
      'Find people likely to click the ad. Buys cheap traffic rather than purchases.',
    destinationSummary: 'sends people to your website',
    reportingGoal: CampaignGoalType.ECommerceSales,
    destinationType: DestinationType.WEBSITE,
    promotedObjectKind: 'none',
    attributionSpec: CLICK_1,
  },
];

/** What a brand-new campaign starts on: the combination the team publishes most. */
export const defaultOptimization: OptimizationDefinition = optimizationCatalog.filter(
  (entry) => entry.key === 'leads.instant_forms',
)[0];

export interface CampaignObjectiveOption {
  objective: OutcomeCampaignObjective;
  label: string;
  icon: string;
  description: string;
}

/**
 * The objective cards, in the order they are shown. Derived order matters: Leads is what the
 * team publishes most, so it leads.
 */
export const campaignObjectives: CampaignObjectiveOption[] = [
  {
    objective: OutcomeCampaignObjective.OUTCOME_LEADS,
    label: 'Leads',
    icon: 'user-plus',
    description: 'Collect contact details, on the ad or on your site.',
  },
  {
    objective: OutcomeCampaignObjective.OUTCOME_SALES,
    label: 'Sales',
    icon: 'cart-arrow-down',
    description: 'Drive purchases and other high-value events on your site.',
  },
  {
    objective: OutcomeCampaignObjective.OUTCOME_TRAFFIC,
    label: 'Traffic',
    icon: 'desktop',
    description: 'Send people from the ad to a page on your site.',
  },
  {
    objective: OutcomeCampaignObjective.OUTCOME_ENGAGEMENT,
    label: 'Engagement',
    icon: 'comments',
    description: 'Drive interaction with the ad, or conversions on your site.',
  },
  {
    objective: OutcomeCampaignObjective.OUTCOME_AWARENESS,
    label: 'Awareness',
    icon: 'store',
    description:
      'Put the ad in front of as many people as possible. Store traffic lives here.',
  },
];

/**
 * How a draft written before objectives existed resolves. `CampaignGoalType` was carrying both
 * the delivery decision and the reporting bucket; these four rows are what it actually published.
 */
export const legacyGoalToOptimizationKey: Record<CampaignGoalType, string> = {
  [CampaignGoalType.InStoreSales]: 'awareness.reach',
  [CampaignGoalType.WebsiteSessions]: 'engagement.offsite_conversions',
  [CampaignGoalType.LeadGeneration]: 'leads.instant_forms',
  [CampaignGoalType.ECommerceSales]: 'sales.offsite_conversions',
};

/** The subset of a campaign config that decides delivery. */
export interface OptimizationSelection {
  goal?: CampaignGoalType;
  objective?: OutcomeCampaignObjective;
  optimizationGoal?: OptimizationGoal;
}

export const findOptimizationDefinition = (
  objective: OutcomeCampaignObjective,
  optimizationGoal: OptimizationGoal,
): OptimizationDefinition | undefined =>
  optimizationCatalog.find(
    (entry) =>
      entry.objective === objective &&
      entry.optimizationGoal === optimizationGoal,
  );

export const getOptimizationDefinitionByKey = (
  key: string,
): OptimizationDefinition | undefined =>
  optimizationCatalog.find((entry) => entry.key === key);

export const optimizationGoalsForObjective = (
  objective: OutcomeCampaignObjective,
): OptimizationDefinition[] =>
  optimizationCatalog.filter((entry) => entry.objective === objective);

/**
 * Pure resolution, shared by the builder and the server: which catalog row a campaign config
 * means. A config carrying an objective is looked up directly; anything else is a draft written
 * before objectives existed and normalizes through the four legacy rows.
 *
 * Returns undefined when the config does not name an optimization the catalog offers: nothing
 * selected at all, only half of an objective/performance-goal pair, or a stored goal outside the
 * legacy four. An unset optimization is a legitimate state for a draft campaign — the AE has not
 * chosen yet — so callers must handle undefined rather than treat it as corruption.
 */
export const resolveOptimizationDefinition = (
  selection: OptimizationSelection,
): OptimizationDefinition | undefined => {
  const hasObjective = !!selection.objective;
  const hasOptimizationGoal = !!selection.optimizationGoal;
  if (hasObjective !== hasOptimizationGoal) return undefined;

  if (selection.objective && selection.optimizationGoal) {
    return findOptimizationDefinition(
      selection.objective,
      selection.optimizationGoal,
    );
  }

  if (!selection.goal) return undefined;

  // Widened so a goal stored outside the legacy four is a lookup miss rather than a type error.
  const legacyKeys: Record<
    string,
    string | undefined
  > = legacyGoalToOptimizationKey;
  const legacyKey = legacyKeys[selection.goal];
  if (!legacyKey) return undefined;

  return getOptimizationDefinitionByKey(legacyKey);
};

/** True when the config still needs the AE to pick a conversion event. */
export const needsConversionSelection = (
  selection: OptimizationSelection & {
    pixelType?: unknown;
    customConversionID?: unknown;
  },
): boolean => {
  const definition = resolveOptimizationDefinition(selection);
  // An unresolvable config needs an objective, not a conversion event; saying otherwise would
  // point the AE at the wrong control.
  if (!definition) return false;
  if (definition.promotedObjectKind !== 'web-event') return false;
  return !selection.pixelType && !selection.customConversionID;
};
